import React, { useMemo, useState, useCallback } from 'react';
import { StyleSheet, Pressable, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { BORDER_RADIUS } from '@/constants/config';
import CollectionDropdown from '@/components/Dropdowns/CollectionDropdown';

const FOLDER_COLOR = '#F59E0B'; // Amber/gold color for folders

const createStyles = (colors, isHovered, isAltBGColor) => StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: 10,
    borderRadius: BORDER_RADIUS.medium,
    backgroundColor: isHovered ? (isAltBGColor ? colors.solid_color_secondary_dark : colors.solid_color_tertiary) : (isAltBGColor ? colors.solid_color_tertiary : colors.solid_color_block),
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    gap: 10,
    transition: 'all 0.15s ease',
  },
  iconWrapper: {
    width: 36,
    height: 36,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: `${FOLDER_COLOR}12`,
  },
  contentArea: {
    flex: 1,
    gap: 2,
  },
  topRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 8,
  },
  collectionName: {
    flex: 1,
    lineHeight: 18,
  },
  metaText: {
    opacity: 0.7,
  },
  optionsButton: {
    padding: 4,
    marginRight: -4,
    borderRadius: BORDER_RADIUS.small,
  },
  optionsButtonHovered: {
    backgroundColor: colors.solid_color_block,
  },
});

const CollectionFolderCard = ({ 
  collection, 
  onPress,
  onRename,
  onDelete,
  onChangeIcon,
  hideMenu = false,
  rightElement = null,
  selected,
}) => {
  const { colors, theme } = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const isAltBGColor = ['sepia', 'muted', 'night', 'smoke'].includes(theme);
  const styles = useMemo(() => createStyles(colors, isHovered, isAltBGColor), [colors, isHovered, isAltBGColor]);
  
  const fileCount = collection.files?.length || 0;
  const chatCount = collection.conversationIds?.length || 0;
  const collectionIcon = collection.icon || 'folder';

  const handlePress = useCallback(() => {
    onPress?.(collection);
  }, [onPress, collection]);

  const handleRename = useCallback(() => {
    onRename?.(collection);
  }, [onRename, collection]);

  const handleDelete = useCallback(() => {
    onDelete?.(collection);
  }, [onDelete, collection]);

  const handleChangeIcon = useCallback(() => {
    onChangeIcon?.(collection);
  }, [onChangeIcon, collection]);

  const handleHoverIn = useCallback(() => setIsHovered(true), []);
  const handleHoverOut = useCallback(() => setIsHovered(false), []);

  return (
    <Pressable
      accessibilityRole={selected === undefined ? 'button' : 'checkbox'}
      accessibilityLabel={selected === undefined ? `Open collection ${collection.title}` : `Select collection ${collection.title}`}
      accessibilityState={selected === undefined ? undefined : { checked: selected }}
      aria-checked={selected}
      style={styles.container}
      onPress={handlePress}
      onHoverIn={handleHoverIn}
      onHoverOut={handleHoverOut}
    >
      <View style={styles.iconWrapper}>
        <Icon 
          name={collectionIcon} 
          size={18} 
          color={FOLDER_COLOR} 
          solid
        />
      </View>

      <View style={styles.contentArea}>
        <View style={styles.topRow}>
          <Text 
            size="s" 
            weight="medium" 
            numberOfLines={2} 
            style={styles.collectionName}
          >
            {collection.title}
          </Text>
          
          {!hideMenu && (
            <Pressable onPress={(e) => e.stopPropagation()}>
              <CollectionDropdown
                TriggerComponent={() => (
                  <Pressable 
                    style={({ hovered }) => [
                      styles.optionsButton,
                      hovered && styles.optionsButtonHovered
                    ]}
                  >
                    <Icon name="ellipsis" size={14} color={colors.text_color_tertiary} />
                  </Pressable>
                )}
                handleChangeIcon={handleChangeIcon}
                handleRename={handleRename}
                handleDelete={handleDelete}
              />
            </Pressable>
          )}
          
          {rightElement}
        </View>
        
        <Text size="xs" color="tertiary" style={styles.metaText}>
          {fileCount} {fileCount === 1 ? 'file' : 'files'}
          {chatCount > 0 && (
            <>
              {' · '}
              {chatCount} {chatCount === 1 ? 'chat' : 'chats'}
            </>
          )}
        </Text>
      </View>
    </Pressable>
  );
};

export default React.memo(CollectionFolderCard);
